<template>
  <template v-if="userStore.isCurrentSuperAdmin">
    <RfBaseCard class="rounded-b-none! border-b-0! p-7.5 pb-0 shadow-none!">
      <div class="mb-7">
        <RfInput
          v-model="query"
          placeholder="Search settings…"
          clearable
          size="small"
          variant="outlined"
          class="max-w-sm"
        />

        <div class="grid grid-cols-2 items-start gap-x-2 px-2">
          <div
            v-for="(column, columnIndex) in flagGroupColumns"
            :key="columnIndex"
            class="flex flex-col gap-2"
          >
            <RfAccordionCard
              v-for="group in column"
              :key="group.title"
              class="px-0! py-2! [&_button]:w-full! [&_button]:justify-between!"
              :title="group.title"
              :opened="isGroupExpanded(group.title)"
              @update:opened="value => setGroupExpanded(group.title, value)"
            >
              <template v-for="subgroup in group.subgroups" :key="subgroup.heading || '_default'">
                <h6 v-if="subgroup.heading" class="text-beige-50 mt-3 mb-1 text-sm font-semibold">
                  {{ subgroup.heading }}
                </h6>
                <div class="flex flex-col gap-1">
                  <RfCheckbox
                    v-for="descriptor in subgroup.items"
                    :key="descriptor.key"
                    v-model="flags[descriptor.key]"
                    :disabled="isFlagDisabled(descriptor)"
                    size="small"
                    @update:modelValue="val => applyMutualExclusion(descriptor, val)"
                  >
                    <span
                      v-for="(segment, index) in highlight(descriptor.label)"
                      :key="index"
                      :class="{ 'bg-yellow rounded-xs': segment.matched }"
                      >{{ segment.text }}</span
                    >
                  </RfCheckbox>
                </div>
              </template>
            </RfAccordionCard>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 items-start gap-x-5 px-2">
        <div
          v-for="(column, columnIndex) in adminSectionColumns"
          :key="columnIndex"
          class="flex flex-col gap-5"
        >
          <RfDashboardCard
            v-for="section in column"
            :key="section.key"
            :ref="sectionRefSetters.get(section.key)"
            :title="section.title"
          >
            <template #body>
              <!-- eslint-disable vue/no-v-html -- description is static app-authored copy, never user input -->
              <p
                v-if="section.description"
                class="mb-3 text-sm text-gray-500"
                v-html="section.description"
              ></p>
              <!-- eslint-enable vue/no-v-html -->
              <div v-if="section.selectFields?.length" class="mb-3 flex flex-col gap-3">
                <RfSelect
                  v-for="descriptor in section.selectFields"
                  :key="descriptor.key"
                  v-model="scalarFieldModel(descriptor).value"
                  :items="descriptor.options"
                  :multiple="descriptor.multiple"
                  :search="descriptor.search"
                  sameWidth
                  variant="outlined"
                  size="small"
                  :title="descriptor.label"
                  :placeholder="descriptor.placeholder"
                />
              </div>
              <div v-if="section.booleanFields?.length" class="flex flex-col gap-1">
                <template v-for="descriptor in section.booleanFields" :key="descriptor.key">
                  <RfCheckbox
                    v-if="isFlagVisible(descriptor)"
                    v-model="scalarFieldModel(descriptor).value"
                    :disabled="isFlagDisabled(descriptor)"
                    size="small"
                  >
                    {{ descriptor.label }}
                  </RfCheckbox>
                  <div
                    v-if="nestedTextFieldsFor(section, descriptor).length"
                    class="mt-1 mb-2 flex flex-col gap-3"
                  >
                    <template
                      v-for="field in nestedTextFieldsFor(section, descriptor)"
                      :key="field.key"
                    >
                      <RfInput
                        v-if="isFlagVisible(field)"
                        v-model.trim="scalarFieldModel(field).value"
                        :title="field.label"
                        :placeholder="field.placeholder"
                        variant="outlined"
                        size="small"
                        :hintMessage="field.hint"
                        :disabled="isFlagDisabled(field)"
                      />
                    </template>
                  </div>
                </template>
              </div>
              <div v-if="section.numberFields?.length" class="mt-3 flex flex-col gap-3">
                <template v-for="descriptor in section.numberFields" :key="descriptor.key">
                  <RfInput
                    v-if="isFlagVisible(descriptor)"
                    v-model.number="scalarFieldModel(descriptor).value"
                    variant="outlined"
                    size="small"
                    :title="descriptor.label"
                    :placeholder="descriptor.placeholder"
                    :hintMessage="descriptor.hint"
                    :errorMessage="fieldErrorMessage(descriptor)"
                    type="number"
                    :step="descriptor.step"
                    :disabled="isFlagDisabled(descriptor)"
                  />
                </template>
              </div>
              <div v-if="section.textFields?.length" class="mt-3 flex flex-col gap-3">
                <template v-for="descriptor in section.textFields" :key="descriptor.key">
                  <RfInput
                    v-if="isFlagVisible(descriptor) && !descriptor.nestedUnder"
                    v-model.trim="scalarFieldModel(descriptor).value"
                    :title="descriptor.label"
                    :placeholder="descriptor.placeholder"
                    variant="outlined"
                    size="small"
                    :hintMessage="descriptor.hint"
                    :disabled="isFlagDisabled(descriptor)"
                  />
                </template>
              </div>
              <div v-for="listField in section.listFields" :key="listField.key" class="mt-3">
                <div class="mb-3 flex flex-col gap-3">
                  <template v-if="listField.fields.length > 1">
                    <RfInput
                      v-for="field in listField.fields"
                      :key="field.key"
                      v-model="listFieldController(listField.key).newItem[field.key]"
                      :title="field.inputTitle"
                      :placeholder="field.placeholder"
                      :requiredTitle="!field.optional"
                      variant="outlined"
                      size="small"
                    />
                  </template>
                  <RfInput
                    v-else
                    v-model="listFieldController(listField.key).newItem"
                    :title="listField.fields[0].inputTitle"
                    :placeholder="listField.fields[0].placeholder"
                    requiredTitle
                    variant="outlined"
                    size="small"
                  />
                  <RfButtonNew
                    type="tertiary"
                    size="small"
                    :disabled="
                      listFieldController(listField.key).canAdd
                        ? false
                        : 'Fill in the required fields first'
                    "
                    @click="listFieldController(listField.key).add()"
                  >
                    {{ listField.addButtonLabel }}
                  </RfButtonNew>
                </div>
                <RfTable
                  v-if="listFieldController(listField.key).items.length"
                  class="[&_td]:px-3! [&_td]:py-1.5! [&_th]:px-3! [&_th]:py-1.5!"
                  :tableData="{ totalCount: listFieldController(listField.key).items.length }"
                  hideTotal
                  noIntersection
                >
                  <template #thead>
                    <tr>
                      <th
                        v-for="field in listField.fields"
                        :key="field.key"
                        class="text-left! font-normal!"
                      >
                        {{ field.columnLabel }}
                      </th>
                      <th class="w-12"></th>
                    </tr>
                  </template>
                  <template #tbody>
                    <tr
                      v-for="(item, index) in listFieldController(listField.key).items"
                      :key="listRowKey(item, index)"
                    >
                      <td v-for="field in listField.fields" :key="field.key">
                        <RfInput
                          v-if="listField.fields.length > 1"
                          v-model="item[field.key]"
                          autoWidth
                          class="w-full"
                          variant="outlined"
                          size="small"
                        />
                        <template v-else>{{ item }}</template>
                      </td>
                      <td class="w-12">
                        <RfButtonNew
                          type="table"
                          @click="listFieldController(listField.key).remove(index)"
                        >
                          <RfCloseIcon class="h-5 w-5" />
                        </RfButtonNew>
                      </td>
                    </tr>
                  </template>
                </RfTable>
              </div>
              <div v-if="section.textareaFields?.length" class="flex flex-col gap-3">
                <RfTextarea
                  v-for="descriptor in section.textareaFields"
                  :key="descriptor.key"
                  v-model="scalarFieldModel(descriptor).value"
                  variant="outlined"
                  rows="10"
                  class="w-full font-mono text-sm"
                  :placeholder="descriptor.placeholder"
                />
              </div>
            </template>
          </RfDashboardCard>
        </div>
      </div>
    </RfBaseCard>

    <RfBaseCard class="mb-5 p-7.5">
      <RfAdminLite />

      <div v-if="pinpointAppExists" class="mt-5 flex flex-wrap content-start items-center gap-4">
        <span class="text-black-3 text-base font-semibold">Enable Push Channels:</span>
        <RfButtonNew type="tertiary" @click="enablePush('adm')">
          <v-icon start>fa:fab fa-amazon</v-icon> ADM
        </RfButtonNew>
        <RfButtonNew type="tertiary" @click="enablePush('fcm')">
          <v-icon start>mdi:mdi-firebase</v-icon> FCM
        </RfButtonNew>
        <RfButtonNew type="tertiary" @click="enablePush('apns')">
          <v-icon start>fa:fab fa-apple</v-icon> APNS
        </RfButtonNew>
        <RfButtonNew type="tertiary" @click="enablePush('sms')">
          <v-icon start>fa:fas fa-sms</v-icon> SMS
        </RfButtonNew>
      </div>
      <div class="mt-5 flex flex-wrap content-start gap-4">
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-dark-blue-1 [.rf-button]:text-white-1"
          @click="triggerJsSdkPublish"
        >
          <v-icon start>fa:fab fa-js</v-icon>
          Publish JS SDK
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button]:text-white-1 [.rf-button:not(.disabled)]:bg-green-50"
          @click="triggerSfmcSync"
        >
          <v-icon start>fa:fab fa-salesforce</v-icon>
          Sync SFMC
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-vermillion-50 [.rf-button]:text-white-1"
          @click="triggerSfmcPurge"
        >
          <RfDeleteIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-5 w-5" />
          Purge SFMC
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button]:text-white-1 [.rf-button:not(.disabled)]:bg-green-50"
          @click="triggerImagewizUpdateCfDistro"
        >
          <v-icon start>md:image</v-icon>
          Update Image Resizer
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button]:text-white-1 [.rf-button:not(.disabled)]:bg-green-50"
          @click="triggerImagewizUpdateCfDistroAll"
        >
          <v-icon start>md:image</v-icon>
          Update Image Resizer All Apps
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button]:text-white-1 [.rf-button:not(.disabled)]:bg-green-50"
          @click="triggerInvalidateCache"
        >
          <RfResetIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-5 w-5" />
          Clear App Cache
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-orange-1 [.rf-button]:text-white-1"
          @click="triggerRefreshTraitValues"
        >
          <RfUpdateIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-5 w-5" />
          Refresh Trait Values
        </RfButtonNew>
        <RfButtonNew
          v-if="!pinpointAppExists"
          class="[.rf-button:not(.disabled)]:bg-beige-40 [.rf-button]:text-white-1"
          @click="triggerCreatePinpointApp"
        >
          <RfPinIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-5 w-5" />
          Create Pinpoint App
        </RfButtonNew>
        <RfButtonNew
          v-if="sampleDataButtonVisible"
          class="[.rf-button:not(.disabled)]:bg-black-2 [.rf-button]:text-white-1"
          @click="triggerSampleData"
        >
          <v-icon start>fa:fas fa-database</v-icon>
          Generate Sample Data
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-beige-40 [.rf-button]:text-white-1"
          @click="triggerCreateSequences"
        >
          <v-icon start>fa:fas fa-stream</v-icon>
          Create Default Guides
        </RfButtonNew>
        <RfButtonNew
          v-if="pinpointAppExists"
          class="[.rf-button:not(.disabled)]:bg-dark-blue-1 [.rf-button]:text-white-1"
          @click="submitEnableEmail"
        >
          <RfEnvelopeIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-5 w-5" />
          Enable Pinpoint Email Channel
        </RfButtonNew>
      </div>
      <div v-if="envHops.length" class="mt-5 flex items-center gap-4">
        <div class="flex items-center gap-2">
          <h4 class="text-black-3 text-base font-semibold">Open in Another Environment:</h4>
          <RfStatusIcon class="fill-shade-3 mb-0.5 h-5! w-5!" />
        </div>
        <div class="flex flex-wrap gap-4">
          <RfButtonNew
            v-for="(hop, index) in envHops"
            :key="hop.url"
            :type="index === 0 ? 'main' : 'secondary'"
            tooltipPos="top"
            tooltipClass="max-w-xs"
            :tooltipMessage="hopWarning"
            @click="openInEnv(hop.url)"
          >
            {{ hop.label }}
          </RfButtonNew>
        </div>
      </div>
      <div class="mt-5 flex items-center gap-5">
        <h4 class="text-heading-2">Danger Zone:</h4>
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-shade-2 [.rf-button]:text-white-1"
          @click="disableEnableAppStart"
        >
          <RfPauseAlterIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-6 w-6" />
          {{ currApp.flags?.disabled ? "Enable" : "Disable" }} {{ currApp.name }}
        </RfButtonNew>
        <RfButtonNew
          class="[.rf-button:not(.disabled)]:bg-vermillion-50 [.rf-button]:text-white-1"
          @click="removeAppStart"
        >
          <RfDeleteIcon class="[.rf-button:not(.disabled)_.rf-icon]:fill-white-1 h-6 w-6" />
          Delete {{ currApp.name }}
        </RfButtonNew>
      </div>

      <RfDialog
        v-if="getModalInfo"
        ref="dangerZoneModal"
        :title="getModalInfo.title"
        :message="getModalInfo.message"
        width="700"
        :confirmText="getModalInfo.actionName"
        @confirm="() => getModalInfo.actions()"
        @close="isDeleting = isDisabling = false"
      >
      </RfDialog>
    </RfBaseCard>
  </template>
</template>

<script setup>
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import { cloneDeep, get as getPath, set as setPath } from "lodash-es";
import ApiApps from "@/apis/ApiApps";
import RfAdminLite from "./RfAdminLite.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfTextarea from "@/components/inputs/RfTextarea.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfCloseIcon from "@/components/icons/RfCloseIcon.vue";
import RfPauseAlterIcon from "@/components/icons/RfPauseAlterIcon.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import RfUpdateIcon from "@/components/icons/RfUpdateIcon.vue";
import RfPinIcon from "@/components/icons/RfPinIcon.vue";
import RfEnvelopeIcon from "@/components/icons/RfEnvelopeIcon.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfStatusIcon from "@/components/icons/RfStatusIcon.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";
import RfAccordionCard from "@/components/cards/RfAccordionCard.vue";
import RfDashboardCard from "@/components/cards/RfDashboardCard.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useUserStore } from "@/pinia/userStore";
import { useAdminsStore } from "@/pinia/adminStore";
import { useEnvHop } from "@/utils/composables/useEnvHop";
import { useAccordionSearch } from "@/utils/composables/useAccordionSearch";
import { useListField } from "@/utils/composables/useListField";
import { useMeasuredColumns } from "@/utils/composables/useMeasuredColumns";
import { toColumns } from "@/utils/gridColumns";
import {
  BOOLEAN_FLAGS,
  ADMIN_SECTIONS,
  isVisible,
  isDisabled,
  normalizeFlags,
  collectFlagDependencies,
  buildFieldRules,
} from "@/utils/adminFlags";

const props = defineProps({ model: { type: Object, required: true } });
const emit = defineEmits(["update:renderKey"]);

const router = useRouter();
const appsStore = useAppsStore();
const toastsStore = useToastsStore();
const adminsStore = useAdminsStore();
const userStore = useUserStore();
const legacyLastErrorStore = useLegacyLastErrorStore();
const { envHops, openInEnv, hopWarning } = useEnvHop();

const apps = computed(() => appsStore.apps);
const dangerZoneModal = useTemplateRef("dangerZoneModal");
const isDeleting = ref(false);
const isDisabling = ref(false);

const currApp = props.model;
const featureSet = ref([...props.model.feature_set]);
const deletingApp = ref(false);
const flags = reactive({
  ...cloneDeep(props.model.flags),
  max_domain_aliases: props.model.flags?.max_domain_aliases ?? 3,
});
// path_filter (and path_filter.users) is optional elsewhere in the app — default both so
// `pathFilter.users.is_limited` below and every `source: "path_filter"` field can assume they exist.
const pathFilter = reactive(cloneDeep(props.model.path_filter) ?? {});
pathFilter.users ??= {};
pathFilter.users.is_limited = pathFilter.users.is_limited ?? false;

// Root objects a section field's `source` can point at, keyed as in adminSchema.json
// ("path_filter" for e.g. Users Limits, which isn't a `flags.<key>` value). Defaults to "flags"
// when a descriptor doesn't declare a source.
const fieldSources = { flags, path_filter: pathFilter };

// Custom Fields and Trait Aliases are edited as arrays (like every other listField), but persist
// as an object keyed by field name / alias key — so unlike the other listFields, their editing
// array isn't `flags[key]` itself. fillModel collapses these into that shape at save time.
const customFieldRows = ref(
  Object.entries(props.model.flags?.subscription_custom_fields || {}).map(
    ([fieldName, config]) => ({
      fieldName,
      traitName: config?.trait_name ?? "",
      defaultValue: config?.default ?? "",
    }),
  ),
);
const traitAliasRows = ref(
  Object.entries(props.model.flags?.trait_aliases || {}).map(([key, value]) => ({ key, value })),
);

const externalTenantId = ref(props.model.external_tenant_id || "");
const aiDesignPrompt = ref(props.model.ai_design_prompt?.trim() || null);

const pinpointAppExists = computed(() => Boolean(currApp.providers?.redfast?.pinpoint_app_id));

const conditionContext = {
  flags,
  app: currApp,
  path_filter: pathFilter,
  // Getter, not a plain value snapshot — isCurrentSuperAdmin is itself a computed, so reading it
  // once here would freeze conditions on whatever the role was at setup time.
  get user() {
    return { isSuperAdmin: userStore.isCurrentSuperAdmin };
  },
};
// Descriptors are stable references (from the static schema), so a computed cached per descriptor
// only re-evaluates its own visibleIf/disabledIf tree when a flag *that tree actually reads*
// changes — instead of every field's condition being re-walked on every render just because some
// unrelated flag elsewhere in the form changed.
const visibleByDescriptor = new WeakMap();
const isFlagVisible = descriptor => {
  if (!visibleByDescriptor.has(descriptor)) {
    visibleByDescriptor.set(
      descriptor,
      computed(() => isVisible(descriptor, conditionContext)),
    );
  }
  return visibleByDescriptor.get(descriptor).value;
};
const applyMutualExclusion = (descriptor, val) => {
  if (val && descriptor.mutuallyExclusiveWith) flags[descriptor.mutuallyExclusiveWith] = false;
};

const disabledByDescriptor = new WeakMap();
const isFlagDisabled = descriptor => {
  if (!disabledByDescriptor.has(descriptor)) {
    disabledByDescriptor.set(
      descriptor,
      computed(() => isDisabled(descriptor, conditionContext)),
    );
  }
  return disabledByDescriptor.get(descriptor).value;
};

// A textField declaring `nestedUnder: <booleanField key>` renders directly beneath that checkbox
// instead of in the section's flat textFields list (e.g. Hard/Soft Limit Users under Users
// Limits) — an explicit schema property rather than inferring the grouping from disabledIf's
// shape, so it stays correct regardless of what condition (if any) actually disables the field,
// and a plain `descriptor.nestedUnder` check is enough to exclude it from the flat list (no
// per-render scan needed). Also hides the nested fields once their own controlling checkbox is
// hidden, so a boolean field that later gains a visibleIf doesn't leave orphaned inputs behind.
const controlledTextFieldsByDescriptor = new WeakMap();
const controlledTextFieldsOf = (section, boolDescriptor) => {
  if (!controlledTextFieldsByDescriptor.has(boolDescriptor)) {
    controlledTextFieldsByDescriptor.set(
      boolDescriptor,
      (section.textFields ?? []).filter(field => field.nestedUnder === boolDescriptor.key),
    );
  }
  return controlledTextFieldsByDescriptor.get(boolDescriptor);
};
const nestedTextFieldsFor = (section, boolDescriptor) =>
  isFlagVisible(boolDescriptor) ? controlledTextFieldsOf(section, boolDescriptor) : [];

// ADMIN_SECTIONS' booleanFields (e.g. Bloom Filters) are flags too — resetIf/normalizeFlags need
// to see them alongside BOOLEAN_FLAGS' generic accordion flags to stay consistent.
const sectionBooleanFlags = ADMIN_SECTIONS.flatMap(section => section.booleanFields ?? []);
const allBooleanFlags = [...BOOLEAN_FLAGS, ...sectionBooleanFlags];

// "Generate Sample Data" only makes sense once its flag is actually on — driven by the same
// visibleIf as the "Enable Sample Data" checkbox itself, instead of a second hand-written
// condition that can drift out of sync with the schema (e.g. missing the is_internal gate).
const sampleDataDescriptor = BOOLEAN_FLAGS.find(
  descriptor => descriptor.key === "sample_data_enabled",
);
const sampleDataButtonVisible = computed(
  () => isFlagVisible(sampleDataDescriptor) && flags.sample_data_enabled,
);

// Non-flags-backed listFields: their editing array is a standalone ref, not flags[key] — see the
// comment above customFieldRows/traitAliasRows.
const nonFlagsListFieldStores = {
  subscription_custom_fields: customFieldRows,
  trait_aliases: traitAliasRows,
};

// One useListField controller per listFields descriptor, created once here rather than from the
// template's v-for — a controller created on every render would reset whatever the user is
// currently typing into its "add new item" input.
const listFieldControllers = new Map(
  ADMIN_SECTIONS.flatMap(section => section.listFields ?? []).map(descriptor => [
    descriptor.key,
    useListField(
      nonFlagsListFieldStores[descriptor.key] ??
        computed({
          get: () => flags[descriptor.key],
          set: value => (flags[descriptor.key] = value),
        }),
      descriptor,
    ),
  ]),
);
const listFieldController = key => listFieldControllers.get(key);

// Multi-field listField rows (Custom Fields, Trait Aliases, Stripe Coupon Override) render an
// editable RfInput per cell, so keying their `v-for` by array index would make Vue reuse a
// row's DOM/input state for whatever item shifts into that index after a splice — e.g. deleting
// row 1 while mid-edit in row 3 would silently rebind that input to row 2's data. Objects keep
// their identity across a splice, so Vue's `:key` can bind directly to the object itself; plain-
// value rows (no per-row input, e.g. Traits Whitelist) have nothing to lose from index reuse and
// fall back to it.
const listRowKey = (item, index) => (item !== null && typeof item === "object" ? item : index);

// Model-backed scalar fields — these live on the model directly rather than under any
// fieldSources root. Anything not registered here falls back to descriptor.source/key below.
const nonFlagsFieldRefs = {
  ai_design_prompt: aiDesignPrompt,
  external_tenant_id: externalTenantId,
  feature_set: featureSet,
};
// Bound to `fieldSources[descriptor.source ?? "flags"]`, read/written via `descriptor.key`
// (dot-paths supported, e.g. "users.hard_limit") — unless the key is registered in
// nonFlagsFieldRefs, for fields that live on the model directly rather than under any root object.
// Also accepts a plain string key (implying source "flags") for callers that don't have a full
// descriptor at hand.
//
// Descriptors come from the static ADMIN_SECTIONS/BOOLEAN_FLAGS schema, so they're stable
// references across renders — cache the computed per descriptor (or per string key) instead of
// building a new one on every template access.
const scalarFieldModelsByDescriptor = new WeakMap();
const scalarFieldModelsByKey = new Map();
const scalarFieldModel = descriptorOrKey => {
  const isKey = typeof descriptorOrKey === "string";
  const descriptor = isKey ? { key: descriptorOrKey } : descriptorOrKey;
  if (nonFlagsFieldRefs[descriptor.key]) return nonFlagsFieldRefs[descriptor.key];

  const cache = isKey ? scalarFieldModelsByKey : scalarFieldModelsByDescriptor;
  const cacheKey = isKey ? descriptorOrKey : descriptor;
  if (!cache.has(cacheKey)) {
    cache.set(
      cacheKey,
      computed({
        get: () => getPath(fieldSources[descriptor.source ?? "flags"], descriptor.key),
        set: value => setPath(fieldSources[descriptor.source ?? "flags"], descriptor.key, value),
      }),
    );
  }
  return cache.get(cacheKey);
};

// RfInput surfaces a single errorMessage string rather than an array of validator functions, so
// each descriptor's declarative rules are run against its current value here — the validator
// array itself is cached per descriptor (same rationale as scalarFieldModel above) since it never
// changes, only its result does. The result is also wrapped in a per-descriptor computed (same
// rationale as isFlagVisible/isFlagDisabled above) so unrelated field edits don't re-run it.
const fieldRulesByDescriptor = new WeakMap();
const fieldErrorMessageByDescriptor = new WeakMap();
const fieldErrorMessage = descriptor => {
  if (!fieldErrorMessageByDescriptor.has(descriptor)) {
    fieldErrorMessageByDescriptor.set(
      descriptor,
      computed(() => {
        const value = scalarFieldModel(descriptor).value;
        // An unset field (never configured for this app) isn't a validation failure — min/range
        // rules would otherwise flag it purely because Number(undefined/null/"") is NaN.
        if (value === undefined || value === null || value === "") return "";

        if (!fieldRulesByDescriptor.has(descriptor)) {
          fieldRulesByDescriptor.set(descriptor, buildFieldRules(descriptor));
        }
        const failure = fieldRulesByDescriptor
          .get(descriptor)
          .map(rule => rule(value))
          .find(result => result !== true);
        return failure ?? "";
      }),
    );
  }
  return fieldErrorMessageByDescriptor.get(descriptor).value;
};

// Boolean flags grouped by section and then by an optional sub-heading, for the feature-flag
// grid. Sections with their own dedicated card (ADMIN_SECTIONS) aren't in BOOLEAN_FLAGS at all.
const featureFlagGroups = computed(() => {
  const bySection = new Map();

  BOOLEAN_FLAGS.forEach(descriptor => {
    if (!isFlagVisible(descriptor)) return;

    if (!bySection.has(descriptor.section)) bySection.set(descriptor.section, new Map());
    const bySubgroup = bySection.get(descriptor.section);

    const subgroupKey = descriptor.group ?? "";
    if (!bySubgroup.has(subgroupKey)) bySubgroup.set(subgroupKey, []);
    bySubgroup.get(subgroupKey).push(descriptor);
  });

  return Array.from(bySection, ([title, bySubgroup]) => ({
    title,
    subgroups: Array.from(bySubgroup, ([heading, groupFlags]) => ({ heading, flags: groupFlags })),
  }));
});

// Same tree, renamed to the generic {title, subgroups: [{heading, items}]} shape
// useAccordionSearch expects — keeps the composable ignorant of "flags"/"checkboxes".
const searchableFlagGroups = computed(() =>
  featureFlagGroups.value.map(group => ({
    title: group.title,
    subgroups: group.subgroups.map(subgroup => ({
      heading: subgroup.heading,
      items: subgroup.flags,
    })),
  })),
);

const { query, filteredGroups, isGroupExpanded, setGroupExpanded, highlight } = useAccordionSearch(
  searchableFlagGroups,
  descriptor => descriptor.label,
);

// Every group renders collapsed by default (just its title bar), so columns should split by group
// count, not by how many flags a group happens to hold underneath — weighting by flag count made
// the two columns look uneven (e.g. 3 tall-looking groups vs. 5 short ones) despite both being the
// same collapsed height. Plain round-robin (the default weight of 1 per item) keeps group counts even.
const flagGroupColumns = computed(() => toColumns(filteredGroups.value, 2));

// Unlike the feature-flag accordion groups above (roughly uniform, so plain round-robin is fine),
// ADMIN_SECTIONS entries vary wildly in rendered height — one checkbox vs. a CRUD table with
// however many rows this particular app has already saved vs. a 10-row textarea. That saved-row
// count is per-app runtime data the schema can't see, so no static weight guessed from field
// counts can balance these correctly — a listField's real height depends on data, not shape.
// useMeasuredColumns renders a rough schema-based guess first (so there's something reasonable
// on screen immediately), then measures real DOM height once after mount and freezes the split —
// one reflow on initial load, never again, so columns don't jump around as the user edits fields.
const initialSectionWeightGuess = section =>
  1 + // card chrome
  (section.description ? 0.7 : 0) +
  (section.selectFields?.length ?? 0) +
  (section.booleanFields?.length ?? 0) * 0.4 +
  (section.numberFields?.length ?? 0) +
  (section.textFields?.length ?? 0) +
  (section.textareaFields?.length ?? 0) * 4 +
  (section.listFields ?? []).reduce((sum, listField) => sum + listField.fields.length + 0.6, 0);

const { columns: adminSectionColumns, setItemRef: setSectionRef } = useMeasuredColumns(
  ADMIN_SECTIONS,
  section => section.key,
  { initialWeightOf: initialSectionWeightGuess },
);
// One bound callback per section, built once — an inline `el => setSectionRef(el, section)` in
// the template would get a new function identity every render, making Vue tear down and rebind
// the :ref on every unrelated re-render instead of only at mount.
const sectionRefSetters = new Map(
  ADMIN_SECTIONS.map(section => [section.key, el => setSectionRef(el, section)]),
);

const getModalInfo = computed(() => {
  if (isDeleting.value)
    return {
      title: `Delete ${currApp.name}?`,
      message: `Deleting an app removes all information about this app including segments, historical data, users and integrations. This cannot be undone.`,
      actions: removeApp,
      actionName: "Delete",
    };
  if (isDisabling.value)
    return {
      title: `${currApp.flags?.disabled ? "Enable" : "Disable"} ${currApp.name}?`,
      message: `${currApp.flags?.disabled ? "Enabling" : "Disabling"} an app will ${currApp.flags?.disabled ? "restore" : "suspend"} all prompts and integrations for ${currApp.name}. The app is currently ${currApp.flags?.disabled ? "disabled" : "enabled"}.`,
      actions: disableEnableApp,
      actionName: currApp.flags?.disabled ? "Enable" : "Disable",
    };
  return null;
});

watch(apps, to => {
  if (deletingApp.value) {
    if (to.length > 0) {
      router.push({ path: `/apps/${to[0].id}/dashboard` });
    } else {
      router.push({ path: "/" });
    }
  }
});

const applyFlagResets = () => {
  const normalized = normalizeFlags(flags, allBooleanFlags, {
    app: currApp,
    path_filter: pathFilter,
    user: conditionContext.user,
  });
  Object.assign(flags, normalized);
};

// Every flag a resetIf condition reads gets tracked here, so adding a new resetIf rule to
// BOOLEAN_FLAGS/ADMIN_SECTIONS is enough — no watcher to hand-wire per flag. A single watcher
// over all of them (rather than one watcher per key) means one user action that touches several
// trigger keys at once still only re-runs the reset pass once per flush, instead of once per key.
const triggerKeys = [
  ...new Set(
    allBooleanFlags
      .filter(descriptor => descriptor.resetIf)
      .flatMap(descriptor => collectFlagDependencies(descriptor.resetIf)),
  ),
];
// Vue's multi-source watch form: pass the getters once, rather than one getter returning a
// `.map()`'d array that would get rebuilt on every reactive flush just for the comparison.
watch(
  triggerKeys.map(key => () => flags[key]),
  () => applyFlagResets(),
);
// collectFlagDependencies only tracks "flags"-sourced reads, so a resetIf keyed on path_filter
// (the same source users.is_limited's disabledIf already uses) wouldn't otherwise re-trigger a
// reset pass. pathFilter is small, so watching it wholesale is cheap.
watch(pathFilter, () => applyFlagResets(), { deep: true });
applyFlagResets();

const disableEnableAppStart = () => {
  isDisabling.value = true;
  nextTick(() => dangerZoneModal.value?.open());
};
const removeAppStart = () => {
  isDeleting.value = true;
  nextTick(() => dangerZoneModal.value?.open());
};
const disableEnableApp = async () => {
  const newApp = cloneDeep(currApp);
  newApp.flags.disabled = !flags?.disabled;
  await appsStore.updateApp({ appObj: newApp });
  emit("update:renderKey");
};
const removeApp = () => {
  deletingApp.value = true;
  appsStore.deleteApp(currApp.id);
};
const triggerSfmcSync = async () => {
  await adminsStore.syncSfmc(props.model.id);
  toastsStore.create({ type: "success", body: "Successfully Sync SFMC" });
};
const triggerSfmcPurge = async () => {
  await adminsStore.purgeSfmc(props.model.id);
  toastsStore.create({ type: "success", body: "Successfully Purged SFMC" });
};
const triggerJsSdkPublish = async () => {
  const response = window.prompt(
    "Enter git SHA to build a preview version, or leave as 'master' for a standard build",
    "master",
  );
  if (!response) return;
  const sourceVersion = response.trim() || "master";
  await adminsStore.publishSdk({
    appId: props.model.id,
    type: "javascript",
    sourceVersion,
  });
  toastsStore.create({
    type: "success",
    body:
      sourceVersion === "master"
        ? "JS SDK build triggered"
        : `Preview build triggered. To preview, add ?rf_brig=${sourceVersion} to the page URL`,
    bodyTransform: null,
    timeout: sourceVersion === "master" ? 3000 : 5000,
  });
};
const triggerImagewizUpdateCfDistro = () => {
  adminsStore.imagewizUpdateCfDistro({ appId: props.model.id, all: false });
};
const triggerImagewizUpdateCfDistroAll = () => {
  adminsStore.imagewizUpdateCfDistro({ appId: props.model.id, all: true });
};
const triggerInvalidateCache = () => {
  adminsStore.invalidateCache(props.model.id);
};
const triggerRefreshTraitValues = async () => {
  const result = await adminsStore.refreshTraitValues(props.model.id);
  if (result?.success === true) {
    toastsStore.create({
      type: "success",
      body: "Trait value refresh started. Values will update shortly.",
    });
  }
};
const triggerCreatePinpointApp = () => {
  adminsStore.createPinpointApp(props.model.id);
};
const triggerSampleData = async () => {
  const result = await adminsStore.createSampleData(props.model.id);
  if (result?.success === true) {
    toastsStore.create({
      type: "success",
      body: "Sample data is being generated. Check the prompt(s) in a few minutes!",
    });
  }
};
const triggerCreateSequences = () => {
  adminsStore.createDefaultSequences(props.model.id);
};
const submitEnableEmail = () => {
  adminsStore.enablePinpointEmails(props.model.id);
};

// Throws a plain Error on invalid numeric input or a duplicate custom-field/trait-alias key —
// callers must catch it (see RfSettings.vue's onSettingsUpdateClick, the sole current caller,
// which wraps this in try/catch and toasts the message).
const fillModel = model => {
  model.feature_set = featureSet.value;

  // Flags whose descriptor sets coerceFalseIfUnset save as `false` instead of `null`/`undefined` —
  // declared per-flag in adminSchema.json rather than hand-listed here, so adding/removing one
  // means editing the schema in one place instead of keeping this array in sync by hand. Scoped to
  // "flags"-sourced descriptors only — a path_filter-sourced one (e.g. users.is_limited) would
  // otherwise write a bogus literal key like flags["users.is_limited"] instead of the real path.
  allBooleanFlags
    .filter(
      descriptor => descriptor.coerceFalseIfUnset && (descriptor.source ?? "flags") === "flags",
    )
    .forEach(({ key }) => {
      if (!flags[key]) flags[key] = false;
    });
  // parseInt(undefined) is NaN, and writing that through would serialize as `null` and clobber an
  // app that's never configured one of these, instead of leaving the key untouched.
  [
    "ping_frequency",
    "metrics_process_count",
    "action_delay",
    "pulse_charts_refresh_seconds",
  ].forEach(key => {
    const parsed = parseInt(flags[key]);
    if (!isNaN(parsed)) flags[key] = parsed;
  });
  const maxDomainAliases = parseInt(flags.max_domain_aliases);
  if (isNaN(maxDomainAliases) || maxDomainAliases < 1) {
    throw new Error("Max Domain Aliases must be at least 1");
  }
  flags.max_domain_aliases = maxDomainAliases;
  if (flags.bloom_filter_enabled) {
    const capacity = parseInt(flags.bloom_filter_capacity);
    const ttl = parseInt(flags.bloom_filter_ttl);
    const errorRate = parseFloat(flags.bloom_filter_error_rate);

    if (isNaN(capacity) || capacity < 1000) {
      throw new Error("Bloom filter capacity must be at least 1,000");
    }
    if (isNaN(errorRate) || errorRate <= 0 || errorRate >= 1) {
      throw new Error("Bloom filter error rate must be between 0 and 1");
    }
    if (isNaN(ttl) || ttl < 3600) {
      throw new Error("Bloom filter TTL must be at least 3,600 seconds (1 hour)");
    }

    flags.bloom_filter_capacity = capacity;
    flags.bloom_filter_ttl = ttl;
    flags.bloom_filter_error_rate = errorRate;
  }
  const customFields = {};
  const seenFieldNameRows = new Map();
  customFieldRows.value.forEach((row, index) => {
    const fieldName = row.fieldName?.trim();
    if (fieldName) {
      if (seenFieldNameRows.has(fieldName)) {
        throw new Error(
          `Duplicate custom field name: "${fieldName}" (rows ${seenFieldNameRows.get(fieldName) + 1} and ${index + 1})`,
        );
      }
      seenFieldNameRows.set(fieldName, index);
      customFields[fieldName] = {
        trait_name: row.traitName?.trim() || fieldName,
        default: row.defaultValue?.trim() || "false",
      };
    }
  });
  flags.subscription_custom_fields = Object.keys(customFields).length ? customFields : undefined;

  const aliases = {};
  const seenAliasKeyRows = new Map();
  traitAliasRows.value.forEach((pair, index) => {
    const key = pair.key?.trim();
    if (key) {
      if (seenAliasKeyRows.has(key)) {
        throw new Error(
          `Duplicate trait alias key: "${key}" (rows ${seenAliasKeyRows.get(key) + 1} and ${index + 1})`,
        );
      }
      seenAliasKeyRows.set(key, index);
      aliases[key] = pair.value?.trim() ?? "";
    }
  });
  flags.trait_aliases = aliases;

  model.path_filter = { ...model.path_filter, ...pathFilter };
  model.flags = { ...model.flags, ...flags };
  model.external_tenant_id = externalTenantId.value?.trim() || null;
  model.ai_design_prompt = aiDesignPrompt.value?.trim() || null;
};

const enablePush = async (channel = "") => {
  try {
    legacyLastErrorStore.setLoading(true);
    await ApiApps.enablePushChannel(props.model.id, channel);
    toastsStore.create({
      type: "success",
      body: `Successfully enabled push channel "${channel.toUpperCase()}"`,
    });
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  } finally {
    legacyLastErrorStore.setLoading(false);
  }
};

defineExpose({
  flags,
  applyMutualExclusion,
  pathFilter,
  featureSet,
  aiDesignPrompt,
  externalTenantId,
  featureFlagGroups,
  fillModel,
  fieldErrorMessage,
  listFieldController,
  isFlagVisible,
  isFlagDisabled,
  scalarFieldModel,
  sampleDataButtonVisible,
  flagGroupColumns,
  controlledTextFieldsOf,
  nestedTextFieldsFor,
});
</script>
